    /**
     * 다중 코드 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
     * 품목 외의 다양한 코드 검색 (거래처, 사원, 창고 등) 기능 제공
     * 엔터 키로 코드 검색 모달 열기
     */

    import { useCallback, useMemo } from "react";
    import { CreateCellDoubleClickHandler, CustomCellDoubleClickAction } from "src/utils/grid/GridHandlerContent";
    import { showWarning } from "src/utils/toast/toastUtil";
    import { 
    DetailTableCore, 
    CodeSearchDetailTableEventsProps, 
    CodeSearchHandlers 
    } from "src/types/detail-table-new";
    import { useDetailTableEvents } from "./useDetailTableEvents";

    export function useDetailTableWithCodeSearch<T extends DetailTableCore>({
    detailList,
    editableColumns = [],
    codeSearchConfigs,
    settingCodeSearchModalProps,
    constraints,
    keyDownConfig,
    }: CodeSearchDetailTableEventsProps<T>): CodeSearchHandlers<T> {

    // 기본 핸들러들 가져오기
    const basicHandlers = useDetailTableEvents({
        detailList,
        editableColumns,
        keyDownConfig,
    });

    /**
     * 다중 코드 검색 더블클릭 핸들러 생성
     */
    const cellDoubleClickActions: CustomCellDoubleClickAction<T>[] = useMemo(() => {
        const actions: CustomCellDoubleClickAction<T>[] = [];

        // 제약 조건 체크 함수
        const isActionAllowed = (field: string): boolean => {
        // 전역 제약 조건
        if (constraints?.notEdit === false) {
            showWarning('편집이 불가능한 상태입니다.');
            return false;
        }
        
        // 필드별 제약 조건
        if (constraints && constraints[field] === false) {
            showWarning(`${field} 필드는 수정이 불가능합니다.`);
            return false;
        }
        
        return true;
        };

        // 각 코드 검색 설정별로 더블클릭 액션 생성
        Object.entries(codeSearchConfigs).forEach(([codeField, config]) => {
        // 코드 필드 자체 더블클릭 (기존 값으로 검색)
        actions.push({
            validate: (field) => field === codeField,
            action: (e, foundData) => {
            if (!isActionAllowed(codeField)) return;
            
            if (settingCodeSearchModalProps) {
                settingCodeSearchModalProps(
                config.category,
                (foundData as any)[codeField] || "",
                false,
                codeField,
                foundData,
                foundData.valueIndex
                );
            }
            }
        });

        // 관련 필드들 더블클릭 (빈 값으로 검색)
        Object.values(config.responseMapping).forEach(targetField => {
            if (targetField !== codeField) {
            actions.push({
                validate: (field) => field === targetField,
                action: (e, foundData) => {
                if (!isActionAllowed(codeField)) return;
                
                if (settingCodeSearchModalProps) {
                    settingCodeSearchModalProps(
                    config.category,
                    "",
                    false,
                    codeField,
                    foundData,
                    foundData.valueIndex
                    );
                }
                }
            });
            }
        });
        });

        return actions;
    }, [codeSearchConfigs, settingCodeSearchModalProps, constraints]);

    const handleCellDoubleClick = CreateCellDoubleClickHandler(detailList, 'valueIndex', cellDoubleClickActions);

    // 키다운 핸들러 확장 (코드 검색 특화)
    const enhancedHandleCellKeyDown = useCallback((event: any) => {
        // 기본 키다운 핸들러 실행
        basicHandlers.handleCellKeyDown(event);

        // 코드 검색 특화 키다운 로직
        const field = event.colDef.field;
        const config = codeSearchConfigs[field];
        
        // 엔터 키로 해당 필드의 코드 검색 모달 열기 (필드가 비어있을 때만)
        if (event.event.key === 'Enter' && config) {
            const foundData = detailList.find(item => item.valueIndex === event.data.valueIndex);
            if (foundData) {
            // 제약 조건 체크
            if (constraints?.notEdit === false) {
                showWarning('편집이 불가능한 상태입니다.');
                return;
            }
            
            if (constraints && constraints[field] === false) {
                showWarning(`${field} 필드는 수정이 불가능합니다.`);
                return;
            }
            // 필드가 비어있을 때만 모달 열기
            const currentValue = (foundData as any)[field];
            if (!currentValue || currentValue.toString().trim() === '') {
                if (settingCodeSearchModalProps) {
                    settingCodeSearchModalProps(
                    config.category,
                    "",
                    false,
                    field,
                    foundData,
                    foundData.valueIndex
                );
                }
            }
            // 필드에 값이 있으면 기본 네비게이션 로직 실행 (다음 셀로 이동)
        }
        }

        // 엔터/탭 키 특화 처리
        if ((event.event.key === 'Enter' || event.event.key === 'Tab') && config) {
        // 코드 검색 필드에서 엔터/탭 시 특별한 포커스 이동 로직 추가 가능
        }   
    }, [basicHandlers.handleCellKeyDown, detailList, codeSearchConfigs, settingCodeSearchModalProps, constraints]);

    return {
        ...basicHandlers,
        handleCellKeyDown: enhancedHandleCellKeyDown,
        handleCellDoubleClick,
    };
    }
